import React, {ChangeEvent} from 'react'
import {Box, FormControl, FormErrorMessage, Select, StackDivider, Text, VStack} from '@chakra-ui/react'
import {getIn, useFormikContext} from 'formik'
import PropTypes from 'prop-types'
import {IDENTITY_DOCUMENT_TYPE_OPTIONS} from '@frnt/base/constants'
import {FileInputField} from '../../file-input-field'

interface IdentityDocumentOption {
  value: string
  label: string
  readonly?: boolean
}

interface FormValues {
  [key: string]: unknown
}

interface SyntheticFieldEvent {
  target: {
    name: string
    value: unknown
  }
}

interface IdFieldsProps {
  name: string
  authToken?: string
  maxSize?: number
  maxFiles?: number
  uploadUri?: string
  disabled?: boolean
  handleBlur?: (event?: unknown) => void
  handleChange?: (event: SyntheticFieldEvent) => void
}

const PASSPORT = 'passport'
const CANCHECK = 'cancheck'
const SINGLE_SIDED_TYPES = [PASSPORT, CANCHECK]
const DEFAULT_MAX_SIZE_MB = 10

const getMaxSizeMb = (maxSize?: number): number => {
  if (!maxSize || Number.isNaN(maxSize)) return DEFAULT_MAX_SIZE_MB
  return Math.max(1, Math.round(maxSize / 1000000))
}

const createSyntheticEvent = (name: string, value: unknown): SyntheticFieldEvent => ({
  target: {
    name,
    value
  }
})

export const IdFields: React.FC<IdFieldsProps> = ({name, maxSize, disabled, handleBlur, handleChange = () => {}}) => {
  const {values, errors, touched, setFieldValue, setFieldTouched} = useFormikContext<FormValues>()
  const typeFieldName = `${name}.type`
  const selectedType = (getIn(values, typeFieldName) as string | undefined) ?? ''
  const isSoloField = SINGLE_SIDED_TYPES.includes(selectedType)
  const maxSizeMb = getMaxSizeMb(maxSize)
  const typeError = getIn(touched, typeFieldName) ? (getIn(errors, typeFieldName) as string | undefined) : undefined

  const handleTypeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value
    setFieldValue(typeFieldName, value)
    if (SINGLE_SIDED_TYPES.includes(value)) {
      setFieldValue(`${name}.back`, undefined)
    }
    handleChange(createSyntheticEvent(typeFieldName, value))
  }

  const handleTypeBlur = (event: ChangeEvent<HTMLSelectElement>) => {
    setFieldTouched(typeFieldName, true)
    setTimeout(() => {
      handleBlur?.(event)
    }, 0)
  }

  const handleFileChange = (event: SyntheticFieldEvent) => {
    handleChange(event)
    setTimeout(() => {
      handleBlur?.(event)
    }, 0)
  }

  return (
    <Box borderWidth="1px" borderColor="gray.200" rounded="lg" bg="white" boxShadow="sm" p={3}>
      <VStack align="stretch" spacing={3} divider={<StackDivider borderColor="gray.100" />}>
        <FormControl isInvalid={!!typeError}>
          <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
            Document type
          </Text>
          <Select
            name={typeFieldName}
            value={selectedType}
            onChange={handleTypeChange}
            onBlur={handleTypeBlur}
            size="sm"
            isDisabled={disabled}
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            boxShadow="sm"
            _hover={{boxShadow: 'sm', borderColor: 'gray.300'}}
            _focusVisible={{bg: 'white', borderColor: 'primary2.500', boxShadow: '0 0 0 1px var(--chakra-colors-primary2-500), var(--chakra-shadows-sm)'}}
            _disabled={{bg: 'gray.100', borderColor: 'gray.200', boxShadow: 'none'}}
          >
            <option value="">Select document type</option>
            {(IDENTITY_DOCUMENT_TYPE_OPTIONS as IdentityDocumentOption[]).map((option) => (
              <option key={option.value} value={option.value} disabled={option.readonly}>
                {option.label}
              </option>
            ))}
          </Select>
          <FormErrorMessage>{typeError}</FormErrorMessage>
        </FormControl>

        <Box>
          <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
            Front side
          </Text>
          <FileInputField name={`${name}.front`} value={getIn(values, `${name}.front`)} maxSize={maxSizeMb} isDisabled={disabled} onChange={handleFileChange} />
        </Box>

        {!isSoloField && (
          <Box>
            <Text fontSize="11px" fontWeight="bold" color="gray.600" mb={1}>
              Back side
            </Text>
            <FileInputField name={`${name}.back`} value={getIn(values, `${name}.back`)} maxSize={maxSizeMb} isDisabled={disabled} onChange={handleFileChange} />
          </Box>
        )}
      </VStack>
    </Box>
  )
}

export const idFieldsPropTypes = {
  name: PropTypes.string.isRequired,
  authToken: PropTypes.string,
  maxSize: PropTypes.number,
  maxFiles: PropTypes.number,
  uploadUri: PropTypes.string,
  disabled: PropTypes.bool,
  handleBlur: PropTypes.func,
  handleChange: PropTypes.func
}

IdFields.propTypes = idFieldsPropTypes
